import {
  getMemberDetail,
  MemberDetailApiError,
  updateMember,
  UpdateMemberApiError,
  type MemberDetailContract,
} from '@urbanik/api-client';
import {
  Button,
  ConfirmationDialog,
  CanonicalPageHeader,
  InfoPanel,
  FormActions,
  FormSection,
  PageActions,
  ProfileSummary,
  QuickActionCard,
  StatePanel,
  StatusBadge,
  InputField,
  canonicalStates,
  formatDateTime,
} from '@urbanik/ui';
import {
  ArrowLeft,
  CalendarDays,
  Eye,
  FileText,
  History,
  Image,
  Mail,
  MapPinned,
  Phone,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { apiBase } from './api-base';
import { ExpandedMemberProfile } from './ExpandedMemberProfile';
import { MemberAddressLocation } from './MemberAddressLocation';
import { MemberHousehold } from './MemberHousehold';
import { MemberPhotoEditor } from './MemberPhotoEditor';
import { MemberNumberEditor } from './MemberNumberEditor';
import { MemberStatusEditor } from './MemberStatusEditor';
import { MemberUpdateAuthorizationCard } from './MemberUpdateAuthorizationCard';
import './member-edit.css';
type Draft = {
  nationalId: string;
  givenNames: string;
  familyNames: string;
  email: string;
  primaryPhone: string;
  secondaryPhone: string;
};
export function EditMemberView({
  memberId,
  canReadPhoto = false,
  canManagePhoto = false,
  canReadLocation = false,
  canManageLocation = false,
  canReadHousehold = false,
  canManageHousehold = false,
  canReadHouseholdPhotos = false,
  canManageHouseholdPhotos = false,
  canReadUpdateAuthorizations = false,
  canManageUpdateAuthorizations = false,
  canManageNumber = false,
  canChangeStatus = false,
}: {
  memberId: string;
  canReadPhoto?: boolean;
  canManagePhoto?: boolean;
  canReadLocation?: boolean;
  canManageLocation?: boolean;
  canReadHousehold?: boolean;
  canManageHousehold?: boolean;
  canReadHouseholdPhotos?: boolean;
  canManageHouseholdPhotos?: boolean;
  canReadUpdateAuthorizations?: boolean;
  canManageUpdateAuthorizations?: boolean;
  canManageNumber?: boolean;
  canChangeStatus?: boolean;
}) {
  const [member, setMember] = useState<MemberDetailContract | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [state, setState] = useState<'loading' | 'error' | 'forbidden' | 'empty' | 'success'>(
    'loading',
  );
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [conflict, setConflict] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setState('loading');
    getMemberDetail(apiBase, memberId, c.signal)
      .then(({ member: m }) => {
        setMember(m);
        setDraft({
          nationalId: m.nationalId,
          givenNames: m.givenNames,
          familyNames: m.familyNames,
          email: m.email ?? '',
          primaryPhone: m.primaryPhone ?? '',
          secondaryPhone: m.secondaryPhone ?? '',
        });
        setConflict(false);
        setProblem('');
        setState('success');
      })
      .catch((e) => {
        if (c.signal.aborted) return;
        if (e instanceof MemberDetailApiError && e.kind === 'unauthorized') {
          window.location.hash = '#login';
          return;
        }
        if (e instanceof MemberDetailApiError && e.kind === 'forbidden') setState('forbidden');
        else if (
          e instanceof MemberDetailApiError &&
          (e.kind === 'not-found' || e.kind === 'validation')
        )
          setState('empty');
        else setState('error');
      });
    return () => c.abort();
  }, [attempt, memberId]);
  if (state !== 'success' || !member || !draft) {
    const def = canonicalStates.find((s) => s.kind === state)!;
    return (
      <div className="shell-page">
        <StatePanel state={def} onAction={() => setAttempt((v) => v + 1)} />
      </div>
    );
  }
  const change = (k: keyof Draft, v: string) => {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
    setProblem('');
    setConflict(false);
  };
  const validate = () =>
    /^\d{6,20}$/.test(draft.nationalId) &&
    Boolean(draft.givenNames.trim()) &&
    Boolean(draft.familyNames.trim()) &&
    (!draft.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email));
  const prepare = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) setReview(true);
    else setProblem('Revisa la cédula, los nombres, los apellidos y el correo.');
  };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await updateMember(apiBase, memberId, {
        ...draft,
        expectedVersion: member.version,
      });
      window.location.hash = `#socios/${result.member.id}`;
    } catch (e) {
      if (e instanceof UpdateMemberApiError && e.kind === 'unauthorized') {
        window.location.hash = '#login';
        return;
      }
      if (e instanceof UpdateMemberApiError && e.kind === 'version-conflict') setConflict(true);
      setProblem(e instanceof Error ? e.message : 'No pudimos actualizar el socio.');
      setReview(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="shell-page member-edit-page">
      <CanonicalPageHeader
        title="Editar socio"
        description="Actualiza desde un solo lugar toda la información administrativa autorizada del socio."
        actions={
          <PageActions>
            <Button
              icon={<ArrowLeft aria-hidden="true" />}
              onClick={() => (location.hash = '#socios')}
            >
              Volver a la lista
            </Button>
          </PageActions>
        }
      />
      <div className="member-edit-layout">
        <main className="member-edit-main">
          {canChangeStatus ? (
            <section className="member-edit-section">
              <MemberStatusEditor key={member.id} member={member} onChanged={setMember} />
            </section>
          ) : null}
          {canManageNumber ? <MemberNumberEditor memberId={memberId} /> : null}
          <form
            id="member-edit-general"
            className="member-create-card member-edit-form"
            onSubmit={prepare}
            noValidate
          >
            <FormSection
              title="1. Identificación protegida"
              description="La cédula, nombres y apellidos se muestran únicamente como referencia."
            >
              <InputField
                label="Cédula"
                value={draft.nationalId}
                disabled
                onChange={(e) => change('nationalId', e.target.value)}
                required
              />
              <InputField
                label="Nombres"
                value={draft.givenNames}
                disabled
                onChange={(e) => change('givenNames', e.target.value)}
                required
                maxLength={100}
              />
              <InputField
                label="Apellidos"
                value={draft.familyNames}
                disabled
                onChange={(e) => change('familyNames', e.target.value)}
                required
                maxLength={100}
              />
            </FormSection>
            <FormSection
              title="2. Información de contacto"
              description="Medios de contacto administrados por URBANIK."
            >
              <InputField
                label="Correo"
                type="email"
                value={draft.email}
                onChange={(e) => change('email', e.target.value)}
                maxLength={254}
              />
              <InputField
                label="Teléfono principal"
                value={draft.primaryPhone}
                onChange={(e) => change('primaryPhone', e.target.value)}
                maxLength={25}
              />
              <InputField
                label="Teléfono secundario"
                value={draft.secondaryPhone}
                onChange={(e) => change('secondaryPhone', e.target.value)}
                maxLength={25}
              />
            </FormSection>
            {problem ? (
              <div
                className={conflict ? 'member-edit-conflict' : 'member-create-error'}
                role="alert"
              >
                <p>{problem}</p>
                {conflict ? (
                  <button
                    className="ui-button ui-button--secondary"
                    type="button"
                    onClick={() => setAttempt((v) => v + 1)}
                  >
                    Recargar versión del servidor
                  </button>
                ) : null}
              </div>
            ) : null}
            <FormActions>
              <a className="ui-button ui-button--secondary" href={`#socios/${memberId}`}>
                Cancelar
              </a>
              <button className="ui-button ui-button--primary" type="submit">
                Revisar cambios
              </button>
            </FormActions>
          </form>
          <section className="member-edit-tools" aria-label="Edición administrativa completa">
            <section id="member-edit-photo" className="member-edit-tool-card">
              <header>
                <span>
                  <Image aria-hidden="true" />
                </span>
                <div>
                  <p>3. Identidad visual</p>
                  <h2>Fotografía del socio</h2>
                  <small>Consulta o actualiza la imagen administrativa.</small>
                </div>
              </header>
              <MemberPhotoEditor
                memberId={memberId}
                name={`${member.givenNames} ${member.familyNames}`}
                canRead={canReadPhoto}
                canManage={canManagePhoto}
              />
            </section>
            <section id="member-edit-profile" className="member-edit-section">
              <header>
                <p>4. Perfil ampliado</p>
                <h2>Datos personales y educativos</h2>
              </header>
              <ExpandedMemberProfile memberId={memberId} canUpdate embedded />
            </section>
            <section id="member-edit-location" className="member-edit-section">
              <header>
                <p>5. Residencia registrada</p>
                <h2>Dirección y ubicación</h2>
              </header>
              {canReadLocation ? (
                <MemberAddressLocation memberId={memberId} canManage={canManageLocation} embedded />
              ) : (
                <InfoPanel title="Dirección y ubicación" icon={<MapPinned aria-hidden="true" />}>
                  La sesión no tiene permiso de lectura.
                </InfoPanel>
              )}
            </section>
            <section id="member-edit-household" className="member-edit-section">
              <header>
                <p>6. Composición familiar</p>
                <h2>Hogar e integrantes</h2>
              </header>
              {canReadHousehold ? (
                <MemberHousehold
                  memberId={memberId}
                  canManage={canManageHousehold}
                  canReadPhotos={canReadHouseholdPhotos}
                  canManagePhotos={canManageHouseholdPhotos}
                  embedded
                />
              ) : (
                <InfoPanel title="Grupo familiar" icon={<UsersRound aria-hidden="true" />}>
                  La sesión no tiene permiso de lectura.
                </InfoPanel>
              )}
            </section>
            <section id="member-edit-authorization" className="member-edit-section">
              <header>
                <p>7. Acceso temporal</p>
                <h2>Actualización autorizada</h2>
              </header>
              {canReadUpdateAuthorizations ? (
                <MemberUpdateAuthorizationCard
                  memberId={memberId}
                  canManage={canManageUpdateAuthorizations}
                  embedded
                />
              ) : (
                <InfoPanel
                  title="Actualización autorizada"
                  icon={<ShieldCheck aria-hidden="true" />}
                >
                  La sesión no tiene permiso de lectura.
                </InfoPanel>
              )}
            </section>
          </section>
        </main>
        <aside className="member-edit-rail" aria-label="Resumen y accesos de edición">
          <ProfileSummary
            avatar={
              <MemberPhotoEditor
                memberId={member.id}
                name={`${member.givenNames} ${member.familyNames}`}
                canRead={canReadPhoto}
                canManage={false}
              />
            }
            name={`${member.givenNames} ${member.familyNames}`}
            code={`Cédula ${member.nationalId}`}
            status={
              <StatusBadge tone={member.status === 'active' ? 'success' : 'neutral'}>
                {member.status === 'active' ? 'Activo' : 'Inactivo'}
              </StatusBadge>
            }
            items={[
              { label: 'Correo', value: member.email ?? 'No registrado', icon: Mail },
              { label: 'Teléfono', value: member.primaryPhone ?? 'No registrado', icon: Phone },
              {
                label: 'Fecha de alta',
                value: formatDateTime(member.createdAt),
                icon: CalendarDays,
              },
              { label: 'Versión', value: member.version, icon: History },
            ]}
          />
          <section className="member-edit-quick" aria-labelledby="member-edit-quick-title">
            <h2 id="member-edit-quick-title">Secciones de edición</h2>
            <div>
              <QuickActionCard
                icon={<Image aria-hidden="true" />}
                title="Fotografía"
                onClick={() =>
                  document
                    .querySelector('#member-edit-photo')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              />
              <QuickActionCard
                icon={<FileText aria-hidden="true" />}
                title="Perfil ampliado"
                onClick={() =>
                  document
                    .querySelector('#member-edit-profile')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              />
              <QuickActionCard
                icon={<MapPinned aria-hidden="true" />}
                title="Dirección y ubicación"
                onClick={() =>
                  document
                    .querySelector('#member-edit-location')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              />
              <QuickActionCard
                icon={<UsersRound aria-hidden="true" />}
                title="Grupo familiar"
                onClick={() =>
                  document
                    .querySelector('#member-edit-household')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              />
              <QuickActionCard
                icon={<ShieldCheck aria-hidden="true" />}
                title="Autorización temporal"
                onClick={() =>
                  document
                    .querySelector('#member-edit-authorization')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              />
              <QuickActionCard
                icon={<Eye aria-hidden="true" />}
                title="Ver detalle del socio"
                onClick={() => (location.hash = `#socios/${member.id}`)}
              />
            </div>
          </section>
        </aside>
      </div>
      <ConfirmationDialog
        open={review}
        busy={busy}
        title="Confirmar cambios"
        description={`Se actualizará a ${draft.familyNames.trim()}, ${draft.givenNames.trim()} desde la versión ${member.version}.`}
        onCancel={() => setReview(false)}
        onConfirm={() => void submit()}
      />
    </div>
  );
}
